import { Checkbox } from '@talentmesh/ui/components/checkbox';
import { cn } from '@talentmesh/ui/lib/utils';
import { useState } from 'react';
import { ShortlistedProfile } from '../../../api/DTO/ShortlistedProfile';
import simplifyShortlistedProfile from '../utils/simplifyShortlistedProfile';
import LinkedInCompanyIcon from '@/Components/TalentMeshIcons/LinkedInCompanyIcon';
import percentileInTextColors from '@/lib/utils';
import UIStrings from '@/Utils/UIStrings';

interface MatchingProfilesListProps {
    profiles: ShortlistedProfile[];
    onSelectedProfiles: (profiles: ShortlistedProfile[]) => void;
}

const MatchingProfilesList = ({ profiles, onSelectedProfiles }: MatchingProfilesListProps) => {
    const [checked, setChecked] = useState<number[]>([]);
    const numChecked = checked.length;
    const showCheck = profiles.length !== 1;

    const handleCheck = (index: number) => {
        const selectedIndex = checked.indexOf(index);
        let newChecked: number[] = [];

        if (selectedIndex === -1) {
            newChecked = newChecked.concat(checked, index);
        } else if (selectedIndex === 0) {
            newChecked = newChecked.concat(checked.slice(1));
        } else if (selectedIndex === checked.length - 1) {
            newChecked = newChecked.concat(checked.slice(0, -1));
        } else if (selectedIndex > 0) {
            newChecked = newChecked.concat(checked.slice(0, selectedIndex), checked.slice(selectedIndex + 1));
        }

        setChecked(newChecked);
        onSelectedProfiles(newChecked.map((i) => profiles[i]));
    };

    const handleCheckAll = (isChecked: boolean) => {
        if (isChecked) {
            const newSelected = profiles.map((_, i) => i);
            setChecked(newSelected);
            onSelectedProfiles(newSelected.map((i) => profiles[i]));
        } else {
            setChecked([]);
            onSelectedProfiles([]);
        }
    };

    return (
        <div className="flex max-w-full overflow-auto transition-opacity duration-200">
            <table
                className="w-full border-separate [border-spacing:0_10px] text-sm"
                aria-label="matching linkedin profiles table"
                data-cy="matchingProfilesTable"
            >
                <thead>
                    <tr className="text-muted-foreground whitespace-nowrap">
                        {showCheck && (
                            <th className="w-10 px-2 text-left font-medium">
                                <Checkbox
                                    checked={
                                        profiles.length > 0 && numChecked === profiles.length
                                            ? true
                                            : numChecked > 0
                                              ? 'indeterminate'
                                              : false
                                    }
                                    onCheckedChange={(v) => handleCheckAll(v === true)}
                                    aria-label="select all"
                                />
                            </th>
                        )}
                        <th className="px-2 text-left font-medium">{UIStrings.Name}</th>
                        <th className="px-2 text-left font-medium">{UIStrings.JobTitle}</th>
                        <th className="px-2 text-left font-medium">{UIStrings.Company}</th>
                        <th className="px-2 text-left font-medium">{UIStrings.Location}</th>
                        <th className="px-2 text-center font-medium">{UIStrings.MatchScore}</th>
                    </tr>
                </thead>
                <tbody>
                    {profiles.map((data, i) => {
                        const isItemSelected = checked.includes(i);
                        const labelId = `checkbox-${i}`;
                        const simpleProfile = simplifyShortlistedProfile(data);

                        return (
                            <tr
                                role="checkbox"
                                aria-checked={isItemSelected}
                                tabIndex={-1}
                                key={data.id}
                                className="bg-muted/60 hover:bg-muted h-[72px] transition-colors duration-150"
                            >
                                {showCheck && (
                                    <td className="rounded-l-md px-2 align-middle">
                                        <Checkbox
                                            checked={isItemSelected}
                                            id={labelId}
                                            onCheckedChange={() => handleCheck(i)}
                                            aria-labelledby={labelId}
                                        />
                                    </td>
                                )}
                                <td className={cn('px-2 align-middle', showCheck ? '' : 'rounded-l-md')} id={labelId}>
                                    <div className="flex max-w-[240px] items-center gap-1 overflow-hidden text-ellipsis whitespace-nowrap">
                                        <span>{simpleProfile.name}</span>
                                        <a
                                            href={data.profile.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex shrink-0 transition-opacity duration-150 hover:opacity-80"
                                        >
                                            <LinkedInCompanyIcon />
                                        </a>
                                    </div>
                                </td>
                                <td className="px-2 align-middle">{simpleProfile.jobTitle || '-'}</td>
                                <td className="px-2 align-middle">{data.profile.currentCompanyName || '-'}</td>
                                <td className="px-2 align-middle">{simpleProfile.location || '-'}</td>
                                <td
                                    className={cn(
                                        'rounded-r-md px-2 text-center align-middle font-semibold',
                                        data.matchScore?.scoreLevel &&
                                            percentileInTextColors[data.matchScore.scoreLevel],
                                    )}
                                >
                                    {typeof data.matchScore === 'number'
                                        ? data.matchScore
                                        : data.matchScore?.score || '-'}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default MatchingProfilesList;
